import { Card, CardContent } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { IMetricDefinition } from '@skystone/shared';
import { HowToUseTips } from './HowToUseTips';
import { TierList } from './TierList';

// ---------------------------------------------------------------------------
// Per-group top-border colour
// ---------------------------------------------------------------------------

const GROUP_BORDER_CLASS: Record<IMetricDefinition['groupKey'], string> = {
  engineering: 'border-t-4 border-t-primary',
  product: 'border-t-4 border-t-accent',
  code_quality: 'border-t-4 border-t-success',
  governance: 'border-t-4 border-t-warning',
};

// ---------------------------------------------------------------------------
// MetricCard
// ---------------------------------------------------------------------------

interface MetricCardProps {
  definition: IMetricDefinition;
}

function CardBodyColumn({
  label,
  children,
}: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <div className="p-4">
      <p className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      {children}
    </div>
  );
}

// "How it works in practice" (calculation) + "Watch out" (watchOut) — the deep
// content shown only on the Definitions Catalogue card (PRODOPSAI-654). Both
// are optional; the whole block is omitted when neither is present so cards
// without the prose keep their original two-row layout.
function PracticeAndWatchOut({
  calculation,
  watchOut,
}: Readonly<{ calculation?: string; watchOut?: string }>) {
  // Boolean() per field (not `calculation ?? watchOut`): a falsy-but-defined
  // calculation (e.g. '' from the admin preview form) must not suppress a
  // populated watchOut, which `??` would do since it only coalesces null/undefined.
  const hasContent = Boolean(calculation) || Boolean(watchOut);
  if (!hasContent) return null;
  return (
    <div className="space-y-3 border-t border-border p-4">
      {calculation ? (
        <div className="rounded-lg bg-muted/50 p-3">
          <p className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            <span aria-hidden="true">▷</span> How it works in practice
          </p>
          <p className="whitespace-pre-line text-sm leading-relaxed text-foreground">{calculation}</p>
        </div>
      ) : null}
      {watchOut ? (
        <div className="rounded-lg border border-warning/40 bg-warning/10 p-3">
          <p className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-warning">
            <span aria-hidden="true">⚠</span> Watch out
          </p>
          <p className="whitespace-pre-line text-sm leading-relaxed text-foreground">{watchOut}</p>
        </div>
      ) : null}
    </div>
  );
}

export function MetricCard({ definition }: Readonly<MetricCardProps>) {
  const borderClass = GROUP_BORDER_CLASS[definition.groupKey];
  return (
    <Card className={cn('flex h-full flex-col overflow-hidden', borderClass)}>
      <div className="flex items-start gap-3 p-4">
        <div
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-muted text-xl"
          aria-hidden="true"
        >
          {definition.icon}
        </div>
        <div className="flex-1">
          <h3 className="text-base font-bold text-foreground">{definition.name}</h3>
          <p className="text-sm italic text-muted-foreground">{definition.tagline}</p>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            {definition.definition}
          </p>
        </div>
      </div>
      <PracticeAndWatchOut calculation={definition.calculation} watchOut={definition.watchOut} />
      <CardContent className="grid flex-1 grid-cols-1 gap-0 border-t border-border p-0 pt-0 md:grid-cols-2 md:divide-x md:divide-border">
        <CardBodyColumn label="Industry benchmarks">
          <TierList tiers={definition.tiers} />
        </CardBodyColumn>
        <CardBodyColumn label="How to use this metric">
          <HowToUseTips tips={definition.howToUseTips} />
        </CardBodyColumn>
      </CardContent>
    </Card>
  );
}
